import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../lib/api.js';

/** Small shared presentational pieces. */

export function Card({
  title,
  actions,
  children,
  tight,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <section className={className ? `card ${className}` : 'card'}>
      {(title || actions) && (
        <header className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={tight ? 'card-body tight' : 'card-body'}>{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
}): JSX.Element {
  const colour =
    tone === 'ok'
      ? 'var(--ok)'
      : tone === 'warn'
        ? 'var(--warn)'
        : tone === 'danger'
          ? 'var(--danger)'
          : undefined;
  return (
    <div className="card stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={colour ? { color: colour } : undefined}>
        {value}
      </div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  );
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent' | 'outline';

export function Pill({
  tone = 'neutral',
  children,
  dot,
  pulse,
}: {
  tone?: Tone;
  children: ReactNode;
  dot?: boolean;
  pulse?: boolean;
}): JSX.Element {
  return (
    <span className={`pill ${tone}`}>
      {dot && <span className={pulse ? 'dot pulse' : 'dot'} />}
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  DRAFT: 'outline',
  IN_REVIEW: 'warn',
  APPROVED: 'accent',
  PUBLISHED: 'ok',
  REJECTED: 'danger',
  SUPERSEDED: 'neutral',
  ARCHIVED: 'neutral',
  SCHEDULED: 'accent',
  QUEUED: 'neutral',
  RUNNING: 'accent',
  SUCCEEDED: 'ok',
  PARTIAL: 'warn',
  FAILED: 'danger',
  CANCELLED: 'neutral',
  PENDING: 'outline',
  SKIPPED: 'neutral',
  DRY_RUN: 'accent',
  NEEDS_CONSENT: 'warn',
};

/** Renders a status enum with a consistent colour across every screen. */
export function StatusPill({ status }: { status: string }): JSX.Element {
  const tone = STATUS_TONES[status] ?? 'neutral';
  const live = status === 'RUNNING';
  return (
    <Pill tone={tone} dot={live} pulse={live}>
      {status.replace(/_/g, ' ').toLowerCase()}
    </Pill>
  );
}

export function Banner({
  tone = 'info',
  icon,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  icon?: string;
  children: ReactNode;
}): JSX.Element {
  const defaultIcon = { info: 'i', warn: '!', danger: '!', ok: 'ok' }[tone];
  return (
    <div className={`banner ${tone}`}>
      <span className="banner-icon" aria-hidden="true">
        {icon ?? defaultIcon}
      </span>
      <div>{children}</div>
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ rows = 3, label }: { rows?: number; label?: string }): JSX.Element {
  return (
    <div className="col" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label ?? 'Loading'}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${100 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }): JSX.Element {
  const apiError = error instanceof ApiError ? error : null;
  const message = error instanceof Error ? error.message : String(error);

  return (
    <Banner tone={apiError?.isForbidden ? 'warn' : 'danger'}>
      <strong>{apiError?.isForbidden ? 'Not permitted' : 'Something went wrong'}</strong>
      <div>{message}</div>
      {apiError && apiError.issues.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {apiError.issues.map((issue) => (
            <li key={issue.path} className="tiny">
              <code>{issue.path}</code>: {issue.message}
            </li>
          ))}
        </ul>
      )}
      {onRetry && (
        <button className="sm mt" onClick={onRetry} style={{ marginTop: 8 }}>
          Try again
        </button>
      )}
    </Banner>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={wide ? 'modal wide' : 'modal'}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="ghost icon" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

// --- Toasts ----------------------------------------------------------------

export interface Toast {
  id: number;
  tone: 'info' | 'success' | 'error';
  title: string;
  detail?: string;
}

interface ToastApi {
  push: (toast: Omit<Toast, 'id'>) => void;
  success: (title: string, detail?: string) => void;
  error: (title: string, detail?: string) => void;
  /** Reports an unknown thrown value as a toast, without swallowing detail. */
  fromError: (err: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...toast, id }]);
    // Errors stay longer: they usually need reading, not glancing at.
    const ttl = toast.tone === 'error' ? 9000 : 4500;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ttl);
  }, []);

  const value: ToastApi = {
    push,
    success: (title, detail) => push({ tone: 'success', title, ...(detail ? { detail } : {}) }),
    error: (title, detail) => push({ tone: 'error', title, ...(detail ? { detail } : {}) }),
    fromError: (err, fallback = 'Request failed') => {
      const message = err instanceof Error ? err.message : String(err);
      push({ tone: 'error', title: fallback, detail: message });
    },
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <div className="body">
              <div className="title">{t.title}</div>
              {t.detail && <div className="tiny muted">{t.detail}</div>}
            </div>
            <button
              className="ghost icon"
              aria-label="Dismiss"
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// --- Small helpers ---------------------------------------------------------

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

export function Check({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}): JSX.Element {
  return (
    <div>
      <label className="check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {hint && <div className="hint" style={{ marginTop: -4, marginBottom: 8 }}>{hint}</div>}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="seg" role="group">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={o.value === value ? 'on' : ''}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Relative time that degrades gracefully for nulls. */
export function When({ iso, empty = '—' }: { iso: string | null | undefined; empty?: string }): JSX.Element {
  if (!iso) return <span className="muted">{empty}</span>;
  const date = new Date(iso);
  return (
    <span title={date.toLocaleString()} className="nowrap">
      {relativeTime(date)}
    </span>
  );
}

export function relativeTime(date: Date): string {
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  const future = seconds < 0;
  const abs = Math.abs(seconds);

  const units: Array<[number, string]> = [
    [60, 'second'],
    [3600, 'minute'],
    [86_400, 'hour'],
    [604_800, 'day'],
    [2_629_800, 'week'],
    [31_557_600, 'month'],
  ];

  if (abs < 45) return future ? 'in a moment' : 'just now';
  for (let i = 0; i < units.length; i += 1) {
    const [limit, name] = units[i]!;
    if (abs < limit) {
      const divisor = i === 0 ? 1 : units[i - 1]![0];
      const n = Math.round(abs / divisor);
      return future ? `in ${n} ${name}${n === 1 ? '' : 's'}` : `${n} ${name}${n === 1 ? '' : 's'} ago`;
    }
  }
  const years = Math.round(abs / 31_557_600);
  return future ? `in ${years}y` : `${years}y ago`;
}

export function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
}
